// Small buffer to guard against subpixel rounding that could leave text barely truncated.
const SUBPIXEL_BUFFER = 2;

const AUTOSIZE_MAX_WIDTH = 600;

type AutosizeOptions = {
  minWidth?: number;
  maxWidth?: number;
  /**
   * Total width (in px) of the header button region (sort + menu buttons + gaps + padding).
   * This is computed dynamically by the caller based on the grid's headerHeight setting
   * and whether sort/menu buttons are visible for this column.
   *
   * When omitted, defaults to 0 (no buttons — e.g., mobile or column menu disabled).
   */
  headerButtonWidth?: number;
};

/**
 * Measures the unconstrained content width of a single DOM cell by cloning it into a
 * hidden offscreen container with all width/overflow constraints removed.
 *
 * The clone's `offsetWidth` includes the cell's own CSS padding (e.g., px-2 = 8px each side),
 * all renderer-internal padding (chip padding, flag gaps, icon sizing, etc.), and the actual
 * text/content width. This means no additional "body padding" constant is needed.
 *
 * For `<th>` header clones: absolutely-positioned children (action buttons, resize handle,
 * drop indicators) are removed before measurement so only the text+padding width is captured.
 * The caller provides `headerButtonWidth` to account for the button region.
 *
 * @param cell - The `<th>` or `<td>` element to measure
 * @param offscreenContainer - A pre-created, already-appended offscreen div
 * @returns The unconstrained content width in pixels
 */
function measureCellContentWidthViaClone(
  cell: HTMLElement,
  offscreenContainer: HTMLDivElement
): number {
  const clone = cell.cloneNode(true) as HTMLElement;

  clone.style.width = 'auto';
  clone.style.maxWidth = 'none';
  clone.style.minWidth = '0';
  clone.style.overflow = 'visible';
  clone.style.height = 'auto';
  clone.style.maxHeight = 'none';
  clone.style.position = 'static';
  clone.style.whiteSpace = 'nowrap';

  // For <th> clones: remove absolutely-positioned overlay children (resize handle,
  // drop indicators, pinned indicator) that inflate the bounding box in static flow.
  const absSpans = clone.querySelectorAll<HTMLElement>('span[style*="position: absolute"]');
  absSpans.forEach((s) => s.remove());

  // Remove the absolutely-positioned action-buttons div (sort + menu buttons).
  const absButtonDiv = clone.querySelector<HTMLElement>('.absolute.right-0');
  if (absButtonDiv) {
    absButtonDiv.remove();
  }

  // For the inner text wrapper in <th>: remove the paddingRight override that reserved
  // space for action buttons (we account for buttons via headerButtonWidth instead).
  const innerTextDiv = clone.querySelector<HTMLElement>('[style*="padding-right"]');
  if (innerTextDiv) {
    innerTextDiv.style.paddingRight = '0';
  }

  offscreenContainer.appendChild(clone);
  const measuredWidth = clone.offsetWidth;
  offscreenContainer.removeChild(clone);

  return measuredWidth;
}

/**
 * Measures the maximum content width of all rendered cells in a given column.
 *
 * Uses offscreen cloning to read the true unconstrained `offsetWidth` of each cell,
 * which already includes all CSS padding and renderer-internal spacing (chips, flags, etc.).
 *
 * For header cells (`<th>`), the action button region (sort + menu) is removed before
 * cloning and added back via the `headerButtonWidth` option. This makes the measurement
 * fully dynamic — no hardcoded padding constants.
 *
 * A small `SUBPIXEL_BUFFER` (2px) is added to prevent subpixel rounding from causing
 * truncation at boundary widths.
 *
 * @param table - The rendered `<table>` element to scan
 * @param columnKey - The `data-column` attribute value to query
 * @param options - Optional minWidth / maxWidth / headerButtonWidth
 */
export function measureColumnContentWidth(
  table: HTMLTableElement,
  columnKey: string,
  options?: AutosizeOptions
): number | null {
  const cells = table.querySelectorAll<HTMLElement>(`[data-column="${columnKey}"]`);

  if (cells.length === 0) {
    return null;
  }

  const offscreenContainer = document.createElement('div');
  offscreenContainer.style.cssText =
    'position:absolute;visibility:hidden;overflow:visible;top:-9999px;left:-9999px;' +
    'width:auto;height:auto;pointer-events:none;white-space:nowrap;';
  document.body.appendChild(offscreenContainer);

  const headerButtonWidth = options?.headerButtonWidth ?? 0;
  let maxBodyWidth = 0;
  let maxHeaderTextWidth = 0;

  cells.forEach((cell) => {
    const contentWidth = measureCellContentWidthViaClone(cell, offscreenContainer);
    if (cell.tagName === 'TH') {
      if (contentWidth > maxHeaderTextWidth) {
        maxHeaderTextWidth = contentWidth;
      }
    } else {
      if (contentWidth > maxBodyWidth) {
        maxBodyWidth = contentWidth;
      }
    }
  });

  document.body.removeChild(offscreenContainer);

  // Header effective width = text clone width + dynamic button region width.
  // Body effective width = clone width (already includes td padding + content).
  const headerEffectiveWidth = maxHeaderTextWidth > 0 ? maxHeaderTextWidth + headerButtonWidth : 0;
  let result = Math.max(maxBodyWidth, headerEffectiveWidth) + SUBPIXEL_BUFFER;

  if (options?.minWidth != null && result < options.minWidth) {
    result = options.minWidth;
  }

  const effectiveMax = options?.maxWidth ?? AUTOSIZE_MAX_WIDTH;
  if (result > effectiveMax) {
    result = effectiveMax;
  }

  return result;
}
